
import { CommonClass } from './CommonClass';
import { MouseClass } from './MouseInteraction';
import { AutoDriver } from './AutoDriver';
import { Output } from './Output';

export interface WebGLManagerOptions {
  $wrapper: HTMLElement;
  autoDemo?: boolean;
  autoSpeed?: number;
  autoIntensity?: number;
  takeoverDuration?: number;
  autoResumeDelay?: number;
  autoRampDuration?: number;
}

export interface LiquidEtherWebGL {
  output?: { simulation?: { options: any; resize: () => void } };
  autoDriver?: {
    enabled: boolean;
    speed: number;
    resumeDelay: number;
    rampDurationMs: number;
    mouse?: { autoIntensity: number; takeoverDuration: number };
    forceStop: () => void;
  };
  resize: () => void;
  start: () => void;
  pause: () => void;
  dispose: () => void;
}

export class WebGLManager implements LiquidEtherWebGL {
  props: WebGLManagerOptions;
  Common = CommonClass;
  Mouse = MouseClass;
  output!: Output;
  autoDriver?: AutoDriver;
  lastUserInteraction = performance.now();
  running = false;
  private _loop = this.loop.bind(this);
  private _resize = this.resize.bind(this);
  private _onVisibility?: () => void;

  constructor(props: WebGLManagerOptions, colors: string[], isVisibleRef: { current: boolean }) {
    this.props = props;
    
    const Common = new CommonClass();
    const Mouse = new MouseClass();
    
    Common.init(props.$wrapper);
    Mouse.init(props.$wrapper);
    Mouse.autoIntensity = props.autoIntensity || 2.0;
    Mouse.takeoverDuration = props.takeoverDuration || 0.25;
    
    Mouse.onInteract = () => {
      this.lastUserInteraction = performance.now();
      if (this.autoDriver) this.autoDriver.forceStop();
    };

    this.autoDriver = new AutoDriver(Mouse, this, {
      enabled: props.autoDemo || true,
      speed: props.autoSpeed || 0.5,
      resumeDelay: props.autoResumeDelay || 3000,
      rampDuration: props.autoRampDuration || 0.6
    });

    this.init(Common, Mouse, colors);
    
    // Store references locally
    (this as any).Common = Common;
    (this as any).Mouse = Mouse;
    
    window.addEventListener('resize', this._resize);
    this._onVisibility = () => {
      const hidden = document.hidden;
      if (hidden) {
        this.pause();
      } else if (isVisibleRef.current) {
        this.start();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  init(Common: CommonClass, Mouse: MouseClass, colors: string[]) {
    if (!Common.renderer) return;
    this.props.$wrapper.prepend(Common.renderer.domElement);
    this.output = new Output(Common, Mouse, colors);
  }

  resize() {
    const Common = (this as any).Common;
    Common.resize();
    this.output.resize();
  }

  render() {
    const Common = (this as any).Common;
    const Mouse = (this as any).Mouse;
    
    if (this.autoDriver) this.autoDriver.update();
    Mouse.update();
    Common.update();
    this.output.update();
  }

  loop() {
    if (!this.running) return;
    this.render();
    (this as any).rafRef = requestAnimationFrame(this._loop);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._loop();
  }

  pause() {
    this.running = false;
    if ((this as any).rafRef) {
      cancelAnimationFrame((this as any).rafRef);
      (this as any).rafRef = null;
    }
  }

  dispose() {
    try {
      window.removeEventListener('resize', this._resize);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      
      const Mouse = (this as any).Mouse;
      const Common = (this as any).Common;
      
      if (Mouse) Mouse.dispose();
      if (Common.renderer) {
        const canvas = Common.renderer.domElement;
        if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
        Common.renderer.dispose();
      }
    } catch {
      /* noop */
    }
  }
}
